// src/componentes/ChatDetalle.js
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { db, auth } from '../firebase';
import { useOrganization } from '../context/OrganizationContext';
import {
  collection,
  doc,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  getDocs,
  updateDoc,
  setDoc,
  getDoc,
  deleteDoc,
  deleteField,
  serverTimestamp
} from 'firebase/firestore';
import { seguimientoApagado, mostrarInterruptor, escrituraDelInterruptor } from '../utils/interruptorSeguimiento';
import { FaEdit, FaTrash, FaCheck, FaTimes, FaReply, FaPlus, FaArrowLeft, FaHome, FaComments, FaClipboardList, FaPaperPlane } from 'react-icons/fa';
import { waCloudAPI, bloqueoAPI } from '../services/api';
import { useTranslation } from 'react-i18next';
import { formatRelativeTime } from '../utils/alertHelpers';
import { requierePlantilla, UMBRAL_AGENTE_MS } from '../utils/ventanaDeAtencion';
import { abreVentana } from '../utils/mensajeDeCliente';
import { claveDelBannerDePausa } from '../utils/esperaDeRespuesta';
import { ordenarPlantillasDelBanner, veredictoDePlantilla, valoresInicialesDePlantilla, claveDeEtiquetaDeVariable } from './inbox/ordenPlantillas';
import { CANALES_POR_CUENTA, rutaDeEnvio } from './inbox/rutaDeEnvio';
import { cuentaDelChat, numeroIdDeCuenta } from '../utils/cuentaDelChat';
import { orgACambiar } from '../utils/orgDelEnlace';
import { mensajeDeErrorDeEnvio } from '../utils/cuentasDeLaBandeja';
import { estadoSeguimientoVenta, ultimaVenta, totalDeVentas } from '../utils/seguimientoVenta';
import {
  citaDestacada, totalDeReservas,
} from '../utils/seguimientoAgenda';
import {
  estadoSeguimientoTraspaso, motivoDeTraspaso, plazoPrometido, demoraDeTraspaso,
} from '../utils/seguimientoTraspaso';
import { ESTADOS_DE_CHAT, claveDeEtiqueta, estadoEfectivo } from '../utils/estadosDeChat';
import { admiteBloqueoReal } from '../utils/bloqueoDeContacto';
import styles from './ChatDetalle.module.css';
import CuerpoMensaje from './inbox/CuerpoMensaje';
import { esEchoSinTexto } from '../utils/echoDeAppBusiness';
import BotonesDeMensaje from './inbox/BotonesDeMensaje';
import useMediaUrls from './inbox/useMediaUrls';

const API_URL = "https://wppconnect-server-734042103109.us-central1.run.app";

// Configuración de canales con colores oficiales (igual que en Chats.js)
const CHANNEL_CONFIG = {
  whatsapp: {
    key: 'whatsapp',
    label: 'WhatsApp',
    icon: '💬',
    color: '#25D366',
    light: '#dcf8c6',
    dark: '#128C7E'
  },
  instagram: {
    key: 'instagram',
    label: 'Instagram',
    icon: '📸',
    color: '#E1306C',
    light: '#fce4ec',
    dark: '#C13584'
  },
  tiktok: {
    key: 'tiktok',
    label: 'TikTok',
    icon: '🎵',
    color: '#000000',
    secondary: '#EE1D52',
    light: '#f5f5f5',
    dark: '#000000'
  },
  email: {
    key: 'email',
    label: 'Email',
    icon: '📧',
    color: '#4285F4',
    light: '#e3f2fd',
    dark: '#1976D2'
  },
  messenger: {
    key: 'messenger',
    label: 'Messenger',
    icon: '💙',
    color: '#0084FF',
    light: '#e3f2fd',
    dark: '#0066CC'
  }
};

const ChatDetalle = () => {
  const { chatID } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const numeroId = location.state?.numeroId;
  const { context, organizations, switchContext } = useOrganization();
  // El enlace del correo de una derivación trae `?org=` (orgDelEnlace.js): si es otra org de la persona,
  // se cambia a ella, porque el chat se lee bajo la org activa.
  const orgDelEnlace = orgACambiar({ search: location.search, activa: context.orgId, organizaciones: organizations });
  useEffect(() => {
    if (orgDelEnlace) switchContext('organization', orgDelEnlace);
  }, [orgDelEnlace]); // eslint-disable-line react-hooks/exhaustive-deps -- `switchContext` cambia en cada render del proveedor

  const [mensajes, setMensajes] = useState([]);
  const [pendingMensajes, setPendingMensajes] = useState([]);
  const [nuevoMensaje, setNuevoMensaje] = useState('');
  const [estado, setEstado] = useState('');
  const [chatInfo, setChatInfo] = useState(null); // Información del chat (incluye canal)
  // Las reservas de ESTE chat. No viven en el documento del chat a propósito — ver la
  // cabecera de `seguimientoAgenda.js`: cancelar una cita desmarcaría sola, y
  // denormalizarla obligaría a cinco escritores a acordarse.
  const [reservasDelChat, setReservasDelChat] = useState([]);
  // El nombre que Meta verificó para este número. Sale del MISMO `getDoc` que ya hace
  // `loadTemplates` para leer `waba_id`: cero lecturas nuevas.
  const [verifiedName, setVerifiedName] = useState(null);

  // `null` = todavía no se sabe · `true` = la cuenta del chat ya no existe.
  // Se comprueba ANTES de dejar escribir: un chat cuya cuenta se desconectó no se arregla
  // reintentando, así que fallar después de que el agente redacte sería el mismo defecto que
  // este trabajo corrige, sólo que un paso más tarde.
  const [cuentaHuerfana, setCuentaHuerfana] = useState(null);
  const [enviando, setEnviando] = useState(false);
  // ✨ Fase 5: Loading states
  const [loadingMensajes, setLoadingMensajes] = useState(true);
  const [error, setError] = useState(null);
  const [editandoMensajeID, setEditandoMensajeID] = useState(null);
  const [mensajeEditado, setMensajeEditado] = useState('');
  const [mostrarRespuestas, setMostrarRespuestas] = useState(false);
  const [respuestasRapidas, setRespuestasRapidas] = useState([]);
  const [edicionRespuestas, setEdicionRespuestas] = useState(false);
  const [nuevaRespuesta, setNuevaRespuesta] = useState('');
  const [editandoRespuestaID, setEditandoRespuestaID] = useState(null);
  const [textoEditado, setTextoEditado] = useState('');
  // Template state
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateVars, setTemplateVars] = useState({});
  const [sendingTemplate, setSendingTemplate] = useState(false);
  const [conversationExpired, setConversationExpired] = useState(false);
  const mensajesRef = useRef(null);
  const { t } = useTranslation('chats');
  const { t: tc } = useTranslation('common');
  const uid = auth.currentUser?.uid;

  // Helper to get collection paths (org/personal)
  const getChatsPath = () => context.mode === 'organization' && context.orgId
    ? ['organizations', context.orgId, 'chats']
    : ['clientes', uid, 'chats'];

  const getRespuestasPath = () => context.mode === 'organization' && context.orgId
    ? ['organizations', context.orgId, 'respuestasRapidas']
    : ['clientes', uid, 'respuestasRapidas'];

  const getBookingsPath = () => context.mode === 'organization' && context.orgId
    ? ['organizations', context.orgId, 'bookings']
    : ['clientes', uid, 'bookings'];

  /**
   * Las reservas del chat abierto.
   *
   * `where('chatId','==',…)` es un filtro de igualdad sobre un campo único: lo cubre el
   * índice automático, no hace falta ninguno compuesto. NO se filtra por `status` en la
   * consulta — traer sólo las confirmadas escondería las canceladas, y son las que
   * distinguen «esta persona anuló» de «esta persona nunca agendó».
   *
   * ⚠️ AQUÍ DECÍA «una reserva no cambia mientras se mira la conversación, y un oyente
   * más por chat abierto se paga en cada apertura para nada». **Era falso, y ese
   * razonamiento es exactamente lo que produjo el defecto del 24-sep**: se cancela una
   * reunión DESDE la conversación, así que cambia justo mientras se mira.
   *
   * Un comentario que justifica una decisión con una premisa que nadie comprobó es peor
   * que no tenerlo: se lee como si alguien lo hubiera medido.
   */
  useEffect(() => {
    if (!uid || !chatID) return undefined;

    // ⚠️ `onSnapshot` Y NO `getDocs`, desde el 24-sep-2026.
    //
    // EL CASO: Rodrigo canceló una reunión desde el propio chat, el bot se lo confirmó,
    // y la huincha siguió diciendo «📅 Cita agendada» hasta salir y volver a entrar. El
    // dato estaba bien —`cancelBooking` escribió `status: 'cancelled'` al segundo— y la
    // función pura calculaba bien; lo que fallaba era que **esta pantalla tenía una foto**.
    //
    // Es el mismo defecto que la bandeja tenía con los chats, en la colección de al lado.
    //
    // EL `where('chatId')` SE CONSERVA, y no por el tamaño: 22 reservas frente a 37 no
    // ahorra casi nada. Lo que evita es que **un cambio de reserva en CUALQUIER otro chat
    // despierte a este oyente** — con un chat abierto horas, eso sí se nota.
    const parar = onSnapshot(
      query(collection(db, ...getBookingsPath()), where('chatId', '==', chatID)),
      (snap) => setReservasDelChat(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => {
        // Se deja lo último conocido: vaciar la lista borraría una huincha correcta por
        // un fallo de red. La marca de agenda es informativa, no una puerta.
        console.error('[agenda] el oyente de reservas del chat falló:', err?.message || err);
      }
    );

    return () => parar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, chatID, context.mode, context.orgId]);


  // 📌 Función para extraer número del chatID
  const getNumeroLimpio = (id) => {
    if (!id) return '';
    return id.includes('_') ? id.split('_')[1] : id;
  };

  // 📌 Cargar mensajes del chat (estructura unificada)
  useEffect(() => {
    if (!uid || !chatID) return;
    setLoadingMensajes(true);
    setError(null);

    const q = query(
      collection(db, ...getChatsPath(), chatID, 'mensajes'),
      orderBy('timestamp', 'asc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const nuevos = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setMensajes(nuevos);
        setLoadingMensajes(false);
        setTimeout(() => mensajesRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
      },
      (err) => {
        console.error('Error cargando mensajes:', err);
        setError(t('detail.errorLoadMessage'));
        setLoadingMensajes(false);
      }
    );

    return () => unsubscribe();
  }, [chatID, uid, context.mode, context.orgId]);

  // 📌 Suscribirse al chat doc (estado + canal + numeroId + ...).
  // Usamos onSnapshot — no getDoc único — porque el messageRouter hace backfill
  // de campos como numeroId cuando llegan mensajes nuevos, y queremos que la UI
  // (botón template, isWaCloudChat) reaccione inmediatamente al actualizarse.
  useEffect(() => {
    if (!uid || !chatID) return;
    const ref = doc(db, ...getChatsPath(), chatID);
    const unsubscribe = onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setEstado(estadoEfectivo(data));
        setChatInfo(data);
      }
    });
    return () => unsubscribe();
  }, [chatID, uid, context.mode, context.orgId]);

  /**
   * ⚠️ AQUÍ VIVÍA «ABRIR EL CHAT LO MARCA COMO LEÍDO», y se retiró el 24-sep-2026.
   *
   * Ese efecto era el ÚNICO sitio que ponía `mensajesNoLeidos` a cero, y por eso el
   * contador medía «alguien pulsó este chat» en vez de «el cliente espera». La insignia
   * ahora se deriva de `ultimoRemitente` (`utils/esperaDeRespuesta.js`), así que no hay
   * nada que apagar al abrir: la marca se va sola cuando alguien responde.
   *
   * Y se gana algo más: una escritura por cada chat abierto que deja de ocurrir.
   */

  // ¿La cuenta por la que llegó este chat sigue conectada?
  //
  // El censo del 19-ago encontró 8 de 18 chats apuntando a un `channelAccount` que ya no
  // existe: cuentas que se conectaron y luego se desconectaron. El `chatId` y el `accountId`
  // son deterministas, así que esos chats siguen ahí para siempre — y el mecanismo se repite
  // con CADA desconexión, no es una limpieza de una vez.
  useEffect(() => {
    const canal = chatInfo?.canal;
    const accountId = chatInfo?.accountId || chatInfo?.channelAccountId;
    if (!uid || !canal || !CANALES_POR_CUENTA.includes(canal)) { setCuentaHuerfana(null); return; }
    if (!accountId) { setCuentaHuerfana(true); return; }
    let cancelado = false;
    (async () => {
      try {
        // Ruta inline y no `getChannelAccountsPath()`: ese helper se recrea en cada render,
        // así que como dependencia del efecto sería inestable —y añadirlo al array lo
        // dispararía en bucle—. Las dependencias REALES son `mode`, `orgId` y `uid`, que ya
        // están abajo.
        const ruta = context.mode === 'organization' && context.orgId
          ? ['organizations', context.orgId, 'channelAccounts']
          : ['clientes', uid, 'channelAccounts'];
        const snap = await getDoc(doc(db, ...ruta, accountId));
        if (!cancelado) setCuentaHuerfana(!snap.exists());
      } catch (e) {
        // Ante un fallo de lectura NO se bloquea el envío: se deja intentar y que el backend
        // decida. Bloquear por un error de red convertiría un problema transitorio en un
        // chat inutilizable.
        if (!cancelado) setCuentaHuerfana(null);
      }
    })();
    return () => { cancelado = true; };
  }, [chatInfo?.canal, chatInfo?.accountId, chatInfo?.channelAccountId, uid, context.mode, context.orgId]);

  // 📌 Cargar respuestas rápidas
  useEffect(() => {
    if (!uid) return;
    const ref = collection(db, ...getRespuestasPath());
    const unsubscribe = onSnapshot(ref, (snapshot) => {
      const lista = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setRespuestasRapidas(lista);
    });
    return () => unsubscribe();
  }, [uid, context.mode, context.orgId]);

  // Bajo el esquema BSUID el doc id del chat ya NO es el wa_id/teléfono: puede ser un BSUID
  // sanitizado ("CL-4275..."). El destinatario real lo deriva el BACKEND del doc del chat
  // (sendMessage.js: telefono || bsuid). La UI no reinventa esa derivación; sólo necesita
  // saber (a) si hay con qué responder —para habilitar el envío— y (b) un valor de display.
  const looksLikeBsuid = (v) => typeof v === 'string' && /^[A-Z]{2}[.\-][A-Za-z0-9]+$/.test(v.trim());

  // GATE de respuesta de texto libre: ¿el chat tiene ALGÚN identificador enviable? Devuelve
  // teléfono si lo hay, si no el BSUID, si no null. Es sólo la puerta; el backend decide
  // `to` vs `recipient`. Acepta BSUID (antes exigía E.164 y bloqueaba los chats con username).
  const resolveRecipientId = () => {
    const isE164 = (v) => typeof v === 'string' && /^\+?\d{6,15}$/.test(v.trim());
    const tel = chatInfo?.telefono || (isE164(chatID) ? chatID : null);
    if (tel) return String(tel).replace(/^\+/, '');
    if (chatInfo?.bsuid) return chatInfo.bsuid;          // BSUID crudo (con punto)
    return null;
  };

  // Las PLANTILLAS requieren teléfono: el endpoint sendWhatsAppTemplate mete el valor en `to`
  // (no en `recipient`) y las plantillas de autenticación no admiten BSUID de todos modos.
  // Por eso este resolver es E.164-only y devuelve null en chats sólo-BSUID.
  const resolvePhoneForTemplate = () => {
    const isE164 = (v) => typeof v === 'string' && /^\+?\d{6,15}$/.test(v.trim());
    const tel = chatInfo?.telefono || (isE164(chatID) ? chatID : null);
    return tel ? String(tel).replace(/^\+/, '') : null;
  };

  // Helper: envío Cloud API en background. El temp con `tempId` ya debe estar en pendingMensajes.
  // Actualiza el temp según el resultado y devuelve, sin throws (los errores se reflejan en el state).
  const cloudApiSendInternal = async ({ textoLimpio, tempId, recipientPhone, numeroIdToSend, canal = 'whatsapp' }) => {
    const FUNCTIONS_URL = process.env.REACT_APP_FIREBASE_FUNCTIONS_URL || 'https://us-central1-sistema-bks.cloudfunctions.net';
    try {
      const token = await auth.currentUser.getIdToken();
      const res = await fetch(`${FUNCTIONS_URL}/sendMessage`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          canal,
          chatId: chatID,
          recipientId: recipientPhone,
          mensaje: textoLimpio,
          numeroId: numeroIdToSend,
          organizationId: context.orgId || null,
        }),
      });
      if (res.ok) {
        const result = await res.json().catch(() => ({}));
        // Marcar temp con externalId; el useMemo lo filtrará al llegar el doc real por snapshot.
        setPendingMensajes(prev => prev.map(p =>
          p.id === tempId ? { ...p, externalId: result.externalId || null, pending: false, failed: false } : p
        ));
        // Safety: si snapshot no llega en 30s, remover el temp.
        setTimeout(() => {
          setPendingMensajes(prev => prev.filter(p => p.id !== tempId));
        }, 30000);
        return;
      }
      const errData = await res.json().catch(() => ({}));
      if (errData.error === 'WINDOW_EXPIRED') {
        setPendingMensajes(prev => prev.filter(p => p.id !== tempId));
        setNuevoMensaje(textoLimpio);
        setConversationExpired(true);
        return;
      }
      if (errData.error === 'NUMBER_DISCONNECTED') {
        setPendingMensajes(prev => prev.filter(p => p.id !== tempId));
        setNuevoMensaje(textoLimpio);
        alert('El número de WhatsApp está desconectado. Reconéctalo en Canales.');
        return;
      }
      // C4 (coexistence): el número está desconectado desde la app WhatsApp Business o reconectándose
      // (cambio de teléfono). El texto vuelve al cuadro y se dice lo justo para cada estado.
      if (errData.error === 'NUMERO_NO_OPERATIVO') {
        setPendingMensajes(prev => prev.filter(p => p.id !== tempId));
        setNuevoMensaje(textoLimpio);
        alert(t(errData.estado === 'en_reconexion' ? 'detail.bot.numeroEnReconexion' : 'detail.bot.numeroDesconectado'));
        return;
      }
      const isRetryable = res.status >= 500;
      setPendingMensajes(prev => prev.map(p =>
        p.id === tempId
          ? { ...p, pending: false, failed: true, retryable: isRetryable, errorMsg: mensajeDeErrorDeEnvio({ status: res.status, errData, t }) }
          : p
      ));
    } catch (networkErr) {
      // Fetch reject (red caída, abort): localmente reintentable.
      setPendingMensajes(prev => prev.map(p =>
        p.id === tempId
          ? { ...p, pending: false, failed: true, retryable: true, errorMsg: networkErr.message || 'Network error' }
          : p
      ));
    }
  };

  const reintentarMensaje = async (tempId) => {
    const temp = pendingMensajes.find(p => p.id === tempId);
    if (!temp || !temp.retryable || !temp.retryPayload) return;
    setPendingMensajes(prev => prev.map(p =>
      p.id === tempId ? { ...p, pending: true, failed: false, errorMsg: null } : p
    ));
    await cloudApiSendInternal({
      textoLimpio: temp.text,
      tempId,
      recipientPhone: temp.retryPayload.recipientPhone,
      numeroIdToSend: temp.retryPayload.numeroId,
    });
  };

  // 📤 Enviar mensaje unificado (estructura unificada)
  const enviarMensaje = async () => {
    if (!nuevoMensaje.trim() || enviando || !uid || !chatID) return;
    if (cuentaHuerfana === true) return;   // el input ya está inhabilitado; esto cierra la vía del Enter
    setEnviando(true);
    try {
      const canal = chatInfo?.canal || 'whatsapp';
      const textoLimpio = nuevoMensaje.trim().slice(0, 1000);

      // La rama de EMAIL se retiró (19-ago): no hay ni un solo chat de canal `email` en el
      // proyecto —medido sobre las cuatro organizaciones y los doce clientes—, así que era
      // inalcanzable. Además mandaba el `uid` en el cuerpo, que el backend ya ignora desde
      // H-01, y no llevaba `Authorization`: hoy habría muerto en el gate.
      if (isWaCloudChat) {
        // WhatsApp Cloud API: optimistic — pintar temp + limpiar input + dispatch en background.
        // El destinatario real (to vs recipient) lo deriva el backend del doc del chat; aquí
        // sólo comprobamos que HAYA con qué responder. resolveRecipientId acepta BSUID, así que
        // un chat con username (sin teléfono) ya no queda bloqueado.
        const recipientHint = resolveRecipientId();
        if (!recipientHint) {
          alert('No se puede enviar: el chat no tiene teléfono ni BSUID.');
          return;
        }
        const tempId = `temp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        const tempCreatedAt = Date.now();
        const temp = {
          id: tempId,
          text: textoLimpio,
          contenido: textoLimpio,
          direction: 'out',
          direccion: 'saliente',
          tipo: 'texto',
          type: 'text',
          timestamp: { toDate: () => new Date(tempCreatedAt) },
          createdAt: tempCreatedAt,
          pending: true,
          failed: false,
          externalId: null,
          retryable: false,
          retryPayload: { recipientPhone: recipientHint, numeroId: resolvedNumeroId },
        };
        setPendingMensajes(prev => [...prev, temp]);
        setNuevoMensaje('');
        setTimeout(() => mensajesRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
        // Dispatch sin await — el botón se libera en finally inmediatamente; los errores
        // se reflejan en el state del temp.
        cloudApiSendInternal({
          textoLimpio,
          tempId,
          recipientPhone: recipientHint,
          numeroIdToSend: resolvedNumeroId,
        });
      } else if (rutaDeEnvio(canal, { isWaCloudChat }) === 'cuenta') {
        // Instagram / TikTok / Messenger — el defecto que este cambio corrige.
        //
        // ANTES caían en el `else` de abajo y se escribían en Firestore con esquema inglés.
        // Nadie los entregaba: el agente veía su mensaje pintado en el chat y el cliente no
        // lo recibía nunca. No fallaba — CONFIRMABA UN ENVÍO QUE NO OCURRÍA, que es el modo
        // de fallo que cuesta el cliente en vez de una llamada a soporte.
        //
        // Ahora salen por `sendMessage`, con el mismo camino optimista de WhatsApp Cloud: el
        // temp queda `pending` y pasa a `failed` si el envío no sale. El `accountId` NO se
        // manda: lo deriva el backend del documento del chat (ver `sendMessage.js`).
        const destinatario = chatInfo?.remitenteId;
        if (!destinatario) {
          alert(t('detail.errorSendMessage') + 'el chat no tiene remitenteId.');
          return;
        }
        const tempId = `temp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        const tempCreatedAt = Date.now();
        setPendingMensajes(prev => [...prev, {
          id: tempId,
          text: textoLimpio,
          contenido: textoLimpio,
          direction: 'out',
          direccion: 'saliente',
          tipo: 'texto',
          type: 'text',
          timestamp: { toDate: () => new Date(tempCreatedAt) },
          createdAt: tempCreatedAt,
          pending: true,
          failed: false,
          externalId: null,
          retryable: false,
          retryPayload: { recipientPhone: destinatario, numeroId: null, canal },
        }]);
        setNuevoMensaje('');
        setTimeout(() => mensajesRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
        cloudApiSendInternal({
          textoLimpio,
          tempId,
          recipientPhone: destinatario,
          numeroIdToSend: null,
          canal,
        });
      } else if (canal === 'whatsapp' && !esChatDeBaileys(chatInfo)) {
        // WhatsApp SIN cuenta (ni `accountId` ni `numeroId wa_api_*`) y que tampoco es de Baileys:
        // no hay por dónde enviar. Antes caía al camino de Baileys —un `addDoc` que nadie
        // entrega— y se pintaba como enviado (revisión del commit 10, 🟠4). Es el mismo veredicto
        // que daría el servidor (`CHAT_SIN_CUENTA`), adelantado y sin inventar un envío.
        const tempId = `temp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        const tempCreatedAt = Date.now();
        setPendingMensajes(prev => [...prev, {
          id: tempId, text: textoLimpio, contenido: textoLimpio, direction: 'out', direccion: 'saliente',
          tipo: 'texto', type: 'text', timestamp: { toDate: () => new Date(tempCreatedAt) }, createdAt: tempCreatedAt,
          pending: false, failed: true, retryable: false, externalId: null, retryPayload: null,
          errorMsg: mensajeDeErrorDeEnvio({ status: 409, errData: { error: 'CHAT_SIN_CUENTA' }, t }),
        }]);
        setNuevoMensaje('');
        return;
      } else {
        // WhatsApp Baileys: write to Firestore (Baileys handles delivery). Es el único canal
        // que sigue entregando por escritura en Firestore, y por eso conserva su esquema.
        await addDoc(collection(db, ...getChatsPath(), chatID, 'mensajes'), {
          text: textoLimpio,
          direction: 'out',
          from: 'me',
          to: chatID,
          type: 'text',
          timestamp: serverTimestamp(),
          isGroup: false,
          numeroId: numeroId || null,
        });
        setNuevoMensaje('');
      }
      // Un humano intervino: la conversación deja de requerir atención.
      //
      // Es el 90% de los casos y no exige disciplina de nadie — que es lo que
      // hace que el filtro siga sirviendo en el mes tres. Una bandeja de
      // "requiere atención" que sólo crece deja de mirarse a la semana.
      //
      // "Atendido" = un humano respondió, NO = el mensaje se entregó. Si la
      // entrega falla, el agente lo ve en el propio mensaje (queda en failed con
      // reintento) y la conversación ya está en su pantalla, que era el punto de
      // la bandera. Atarla a la entrega la dejaría encendida justo cuando el
      // agente ya está encima del caso.
      marcarAtendido('respuesta_humana');
    } catch (error) {
      console.error('Error al enviar mensaje:', error);
      alert(t('detail.errorSendMessage') + error.message);
    } finally {
      setEnviando(false);
    }
  };

  /**
   * Apaga la bandera de atención del chat. Nunca lanza: no atender bien un
   * "ya lo atendí" no puede romper el envío que lo disparó.
   *
   * @param {'respuesta_humana'|'manual'} via - cómo se resolvió, para poder
   *   distinguir después cuántos casos se cierran respondiendo y cuántos a mano.
   */
  /**
   * Quita la marca de venta del chat.
   *
   * La caducidad a 48 h NO oculta nada: pasa el estado a "vencida" y la marca
   * sigue visible hasta que alguien la saca a mano. Esto es ese "a mano".
   *
   * Se guarda un timestamp en vez de borrar el historial: el historial es el
   * hecho —qué compró y cuándo— y no se toca. Lo que se descarta es el
   * SEGUIMIENTO. Y como `estadoSeguimientoVenta` compara el descarte contra la
   * fecha de la última venta, si el cliente vuelve a comprar la marca reaparece
   * sola, que es lo que se espera de un cliente recurrente.
   */
  const descartarSeguimientoVenta = async () => {
    if (!uid || !chatID) return;
    try {
      const ref = doc(db, ...getChatsPath(), chatID);
      await setDoc(ref, {
        seguimientoVenta: { descartadoEn: new Date().toISOString() },
      }, { merge: true });
    } catch (e) {
      console.error('Error descartando seguimiento de venta:', e);
    }
  };

  const marcarAtendido = async (via) => {
    if (!uid || !chatID) return;
    try {
      const ref = doc(db, ...getChatsPath(), chatID);
      await setDoc(ref, {
        requiereAtencion: false,
        atencionResueltaEn: serverTimestamp(),
        atencionResueltaPor: auth.currentUser?.uid || null,
        atencionResueltaVia: via,
      }, { merge: true });
    } catch (err) {
      console.error('[atencion] no se pudo marcar como atendido:', err);
    }
  };

  const cambiarEstado = async (nuevoEstado) => {
    if (!uid || !chatID) return;
    try {
      // Usar estructura unificada de chats
      const ref = doc(db, ...getChatsPath(), chatID);
      await updateDoc(ref, { estado: nuevoEstado });
      setEstado(nuevoEstado);
      console.log(`Estado actualizado a: ${nuevoEstado}`);
    } catch (err) {
      console.error('Error al actualizar estado:', err);
      alert('Error al actualizar el estado. Por favor intenta de nuevo.');
    }
  };

  // Human takeover: toggle bot pause for THIS conversation only. Uses setDoc
  // with merge because the chat doc may not exist yet if the agent pauses
  // proactively before any inbound message arrives.
  /**
   * BLOQUEAR / DESBLOQUEAR EL CONTACTO.
   *
   * NO es una variante de pausar, y por eso NO comparte su botón ni su fila. Pausar es
   * "esta conversación la atiendo yo"; bloquear es "no quiero saber nada de esta
   * persona" y corta el bot ANTES de las cinco llamadas al modelo. Dejarlos a un clic de
   * distancia habría hecho fácil confundirlos.
   *
   * La confirmación enseña NOMBRE y CONTACTO. Una frase genérica —"¿seguro?"— no da la
   * única información que evita el error que importa: bloquear al contacto equivocado.
   */
  const [bloqueando, setBloqueando] = useState(false);

  const alternarBloqueo = async () => {
    const yaBloqueado = chatInfo?.contactoBloqueado === true;
    // `remitenteId` es el único identificador presente en los cuatro canales (medido:
    // 217/217); `telefono` sólo existe en WhatsApp, y se prefiere porque es el que una
    // persona reconoce de un vistazo.
    const contacto = chatInfo?.telefono || chatInfo?.remitenteId || chatID;
    const nombre = chatInfo?.nombre || chatID;

    // ═══════════════════════════════════════════════════════════════════════════
    //  PEDIR LAS DOS COSAS CUANDO SE PUEDEN PEDIR.
    //
    //  Aquí faltaba `upstream` y el efecto se vio en producción: el bot callaba y los
    //  mensajes seguían llegando al inbox, porque sólo se escribía el marcador local.
    //  Un botón que dice "Bloquear" y no bloquea es peor que no tenerlo: el usuario
    //  deja de vigilar una conversación que sigue entrando.
    //
    //  Donde no se puede —TikTok, Instagram, Messenger, o WhatsApp sin `numeroId`— NO
    //  se pide, y entonces el control se llama Silenciar. La palabra la decide el mismo
    //  predicado que decide el parámetro, así que no pueden desalinearse.
    // ═══════════════════════════════════════════════════════════════════════════
    const real = admiteBloqueoReal(chatInfo);
    const sufijo = real ? 'Block' : 'Silence';

    const confirmado = window.confirm(
      yaBloqueado
        ? t(`block.confirmUn${sufijo.toLowerCase()}`, { nombre, contacto })
        : t(`block.confirm${sufijo}`, { nombre, contacto })
    );
    if (!confirmado) return;

    setBloqueando(true);
    try {
      await bloqueoAPI.alternar({
        chatId: chatID,
        organizationId: context.mode === 'organization' ? context.orgId : null,
        uid,
        bloquear: !yaBloqueado,
        // Al DESbloquear se manda igual, para que Meta quite el bloqueo además del
        // marcador: quitar sólo una de las dos dejaría a la persona sin poder escribir
        // y al asistente respondiendo a un silencio.
        upstream: real,
      });
      // No se toca el estado local: el `onSnapshot` del chat ya trae el cambio, y
      // adelantarlo aquí dejaría la pantalla mintiendo si la escritura fallara después.
    } catch (e) {
      console.error('[Bloqueo] error:', e);
      alert(t('block.error'));
    } finally {
      setBloqueando(false);
    }
  };

  const toggleBotPause = async () => {
    if (!uid || !chatID) return;
    const isPaused = chatInfo?.botPaused === true;
    try {
      const ref = doc(db, ...getChatsPath(), chatID);
      await setDoc(ref, {
        botPaused: !isPaused,
        botPausedAt: isPaused ? null : serverTimestamp(),
        botPausedBy: isPaused ? null : (auth.currentUser?.uid || null),
        // `botResumedAutomatically` NO se escribe: ver CLAUDE.md, "La pausa de un
        // chat no caduca". Ponerlo a `false` aquí borraría, en la siguiente pausa
        // manual, el único rastro que queda de las reanudaciones automáticas.
      }, { merge: true });
    } catch (err) {
      console.error('[BotPause] toggle error:', err);
      alert(t('detail.bot.errorToggle'));
    }
  };

  // Interruptor del seguimiento automático: sólo apaga; reactivar borra el campo (C6). Sin
  // actualizadoEn ni lastTs (C8). Ver utils/interruptorSeguimiento.js.
  const toggleSeguimiento = async () => {
    if (!uid || !chatID) return;
    try {
      await setDoc(doc(db, ...getChatsPath(), chatID), escrituraDelInterruptor(chatInfo, deleteField()), { merge: true });
    } catch (err) {
      console.error('[Seguimiento] toggle error:', err);
      alert(t('detail.seguimiento.error'));
    }
  };

  const formatearFecha = (timestamp) => {
    if (!timestamp) return '';
    const fecha = timestamp.toDate();
    return `${fecha.toLocaleDateString()} ${fecha.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  };

  // ✏️ Edición de mensajes
  const iniciarEdicion = (msg) => {
    setEditandoMensajeID(msg.id);
    setMensajeEditado(msg.text || msg.mensaje || msg.contenido);
  };

  const guardarEdicion = async (msgID) => {
    if (!uid) return;
    try {
      // Use unified chats structure (org/personal)
      const ref = doc(db, ...getChatsPath(), chatID, 'mensajes', msgID);
      await updateDoc(ref, { text: mensajeEditado });
      setEditandoMensajeID(null);
      setMensajeEditado('');
    } catch (error) {
      console.error('Error al editar mensaje:', error);
    }
  };

  const eliminarMensaje = async (msgID) => {
    if (!uid) return;
    if (window.confirm('¿Eliminar este mensaje?')) {
      try {
        // Use unified chats structure (org/personal)
        const ref = doc(db, ...getChatsPath(), chatID, 'mensajes', msgID);
        await deleteDoc(ref);
      } catch (error) {
        console.error('Error al eliminar mensaje:', error);
      }
    }
  };

  // ⚡ Gestión de respuestas rápidas
  const agregarRespuestaRapida = (respuesta) => {
    setNuevoMensaje(respuesta);
    setMostrarRespuestas(false);
  };

  const agregarRespuesta = async () => {
    if (!nuevaRespuesta.trim() || !uid) return;
    // 🚨 IMPORTANT: All resources MUST include organizationId field for Firestore security rules
    await addDoc(collection(db, ...getRespuestasPath()), {
      texto: nuevaRespuesta.trim(),
      organizationId: context.mode === 'organization' ? context.orgId : null,
    });
    setNuevaRespuesta('');
  };

  const guardarRespuesta = async (id) => {
    if (!textoEditado.trim() || !uid) return;
    await updateDoc(doc(db, ...getRespuestasPath(), id), { texto: textoEditado.trim() });
    setEditandoRespuestaID(null);
    setTextoEditado('');
  };

  const eliminarRespuesta = async (id) => {
    if (!uid) return;
    if (window.confirm('¿Eliminar esta respuesta rápida?')) {
      await deleteDoc(doc(db, ...getRespuestasPath(), id));
    }
  };

  // ⚡ Exportar mensajes usando la función correcta
  const [exportando, setExportando] = useState(false);
  const [errorExportar, setErrorExportar] = useState(null);

  const exportarMensajes = async (formato) => {
    if (!uid || !numeroId) return alert("No autenticado");

    setExportando(true);
    setErrorExportar(null);

    try {
      const EXPORTAR_MENSAJES_URL = "https://us-central1-sistema-bks.cloudfunctions.net/exportarMensajes";
      const params = new URLSearchParams({
        clienteID: uid,
        chatID: chatID,
        numeroId: numeroId,
        formato
      });

      const url = `${EXPORTAR_MENSAJES_URL}?${params.toString()}`;
      const idToken = await auth.currentUser.getIdToken();

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${idToken}` }
      });

      if (!response.ok) {
        let errorMsg = "Error al exportar";
        try {
          const errorData = await response.clone().json();
          errorMsg = errorData.error || errorMsg;
        } catch {}
        throw new Error(`${response.status} ${errorMsg}`);
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = `mensajes_${chatID}.${formato}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error("Error exportando mensajes:", err);
      setErrorExportar(err.message || "No se pudo exportar los mensajes.");
    } finally {
      setExportando(false);
    }
  };

  // Check if this chat is WhatsApp Cloud API.
  // EL NÚMERO POR EL QUE SE ENVÍA ES LA CUENTA DEL CHAT, y nada más (commit 10, 23-oct-2026): `cuentaDelChat`
  // (`accountId`, o `numeroId` en los legados), la MISMA regla que el servidor desde el commit 4. Hasta hoy caía
  // al `botPhoneNumberId` del ÚLTIMO entrante: con dos números en la org, un chat legado pelado podía apuntar
  // al número por el que llegó el último mensaje y no al suyo. Sin cuenta, `null`: el envío no se intenta y el
  // servidor lo diría igual (`CHAT_SIN_CUENTA`).
  const resolvedNumeroId = numeroIdDeCuenta(cuentaDelChat(chatInfo));
  // Un chat de Baileys no tiene cuenta y es correcto: su `numeroId` son dígitos (o `tipoConexion: 'web'`).
  const esChatDeBaileys = (info) => info?.tipoConexion === 'web' || /^\d{8,}$/.test(String(info?.numeroId || ''));
  const isWaCloudChat = chatInfo?.canal === 'whatsapp' && !!resolvedNumeroId && resolvedNumeroId.startsWith('wa_api_');

  // ═══════════════════════════════════════════════════════════════════════════
  //  EL BANNER SE ENCIENDE ANTES DE ESCRIBIR, NO DESPUÉS DE FALLAR.
  //
  //  Hasta ahora `conversationExpired` sólo se activaba al recibir el 403 del backend
  //  (más abajo, en el `catch` del envío). Eso deja al operador redactar un mensaje
  //  entero para enterarse al pulsar enviar — y en el caso del 18-sep ni siquiera eso:
  //  Meta devolvió 200 y el rechazo llegó por webhook un segundo después, así que no
  //  hubo 403 que encender nada.
  //
  //  CERO LECTURAS NUEVAS: `mensajes` ya llega por `onSnapshot`, así que el último
  //  entrante del cliente está en memoria. Es un `find` sobre un array que ya existe.
  //
  //  LA REGLA NO SE REIMPLEMENTA: sale de `utils/ventanaDeAtencion`, copia
  //  byte-idéntica de la que usa el backend, con `check-shared-module-parity.js`
  //  bloqueando el commit si divergen. Si la UI y el servidor usaran umbrales
  //  distintos, el banner aparecería antes o después de que el envío falle de verdad
  //  y ninguno de los dos daría un error visible.
  //
  //  ⚠️ SÓLO WHATSAPP CLOUD. Instagram y Messenger tienen su propia rama de banner —sin
  //  botón, porque no hay plantillas con que reabrir— y su ventana se comporta distinto:
  //  medido el 21-sep, tres rechazos por ventana CON LA VENTANA ABIERTA (el cliente
  //  había escrito segundos antes). Encender esto ahí mentiría.
  //
  //  Esto NO sustituye al 403: el backend sigue siendo la autoridad y este estado puede
  //  quedarse rancio si el chat lleva horas abierto. Es un aviso, no una puerta.
  // ═══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!isWaCloudChat) return;
    const ultimoDeCliente = mensajes
      .filter(abreVentana)
      .map((m) => (m.timestamp?.toDate ? m.timestamp.toDate() : (m.timestamp ? new Date(m.timestamp) : null)))
      .filter((d) => d instanceof Date && !Number.isNaN(d.getTime()))
      .sort((a, b) => b - a)[0] || null;
    setConversationExpired(requierePlantilla(ultimoDeCliente, new Date(), UMBRAL_AGENTE_MS));
  }, [mensajes, isWaCloudChat]);
  const waPhoneNumberId = isWaCloudChat ? resolvedNumeroId.replace('wa_api_', '') : null;

  // Merge real messages with optimistic temps. Hide a temp once the real doc with the same
  // externalId arrives, or as a safety net once a real outbound msg with same content lands
  // within 60s of the temp creation (covers the rare case where snapshot beats the HTTP response).
  // URLs firmadas del media visible. El hook NO depende de mensajesParaRender: crea un
  // único IntersectionObserver y los nodos se registran solos, así que los snapshots de
  // Firestore no lo recrean.
  const media = useMediaUrls({
    organizationId: context.isOrganization ? context.orgId : null,
    chatId: chatID,
  });

  const mensajesParaRender = useMemo(() => {
    if (!pendingMensajes.length) return mensajes;
    const realExternalIds = new Set(mensajes.map((m) => m.externalId).filter(Boolean));
    const realOutbound = mensajes.filter((m) => {
      const dir = m.direction || m.direccion;
      return dir === 'out' || dir === 'saliente';
    });
    const visibles = pendingMensajes.filter((p) => {
      if (p.externalId && realExternalIds.has(p.externalId)) return false;
      const match = realOutbound.find((m) => {
        const mText = m.contenido || m.text || m.mensaje;
        if (mText !== p.text) return false;
        const mTs = m.timestamp?.toMillis?.() || m.timestamp?.toDate?.()?.getTime?.() || 0;
        return mTs > 0 && Math.abs(mTs - p.createdAt) < 60000;
      });
      return !match;
    });
    return [...mensajes, ...visibles];
  }, [mensajes, pendingMensajes]);

  // Load templates for WhatsApp Cloud API chats
  const loadTemplates = async () => {
    if (!isWaCloudChat || !waPhoneNumberId || !resolvedNumeroId) return;

    setLoadingTemplates(true);
    try {
      // We need wabaId — get it from the numero doc.
      // `resolvedNumeroId` es la cuenta del chat (`cuentaDelChat`); sin ella no hay número que leer.
      const numerosPath = context.mode === 'organization' && context.orgId
        ? ['organizations', context.orgId, 'numeros', resolvedNumeroId]
        : ['clientes', uid, 'numeros', resolvedNumeroId];
      const numSnap = await getDoc(doc(db, ...numerosPath));
      const wabaId = numSnap.data()?.waba_id;
      // Se guarda ANTES de comprobar el waba: si falta, el modal no se abre, pero no hay
      // motivo para perder un dato que ya está en la mano.
      setVerifiedName(numSnap.data()?.verified_name || null);
      if (!wabaId) throw new Error('WABA ID not found');

      const orgId = context.mode === 'organization' ? context.orgId : null;
      const result = await waCloudAPI.getTemplates({
        phoneNumberId: waPhoneNumberId,
        wabaId,
        organizationId: orgId
      });
      // El filtro a APPROVED es DELIBERADO aqui, y no es el mismo caso que Campanas.jsx.
      // Esto es envio manual desde el inbox: una plantilla no aprobada no se puede
      // enviar, asi que ofrecerla seria ofrecer un boton que falla contra Meta. En
      // Campanas se listan todos los estados porque alli el usuario necesita VER que la
      // plantilla existe y en que estado esta. Mismo literal, significados distintos:
      // que diverjan es correcto, no un olvido que haya que "unificar".
      // El orden lo decide `ordenPlantillas.js`, que existe aparte para poder
      // probarlo: es lo único de este commit que el operador ve.
      setTemplates(ordenarPlantillasDelBanner(
        (result.templates || []).filter(t => t.status === 'APPROVED')
      ));
    } catch (err) {
      console.error('Error loading templates:', err);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const openTemplateModal = () => {
    setShowTemplateModal(true);
    setSelectedTemplate(null);
    setTemplateVars({});
    if (templates.length === 0) loadTemplates();
  };

  const selectTemplate = (tpl) => {
    setSelectedTemplate(tpl);
    // El prerelleno va POR PLANTILLA y no por posición: en `appointment_reminder` la
    // variable 1 es el nombre del CLIENTE —su ejemplo es «Rodrigo»—, no el del negocio.
    // Ver `ordenPlantillas.valoresInicialesDePlantilla`.
    //
    // Y sigue sin haber valor genérico por defecto: lo que no se prerellena queda vacío,
    // y `handleSendTemplate` se niega a enviar con cualquier variable en blanco.
    setTemplateVars(valoresInicialesDePlantilla(tpl, verifiedName));
  };

  const getTemplatePreview = () => {
    if (!selectedTemplate) return '';
    const bodyComp = selectedTemplate.components?.find(c => c.type === 'BODY');
    let text = bodyComp?.text || '';
    Object.entries(templateVars).forEach(([key, val]) => {
      text = text.replace(`{{${key}}}`, val || `{{${key}}}`);
    });
    return text;
  };

  const handleSendTemplate = async () => {
    if (!selectedTemplate || !waPhoneNumberId || sendingTemplate) return;

    // Validate that every detected variable has a non-empty value.
    // selectTemplate populates templateVars with one key per {{N}} placeholder in the body.
    const missingVars = Object.entries(templateVars)
      .filter(([, val]) => !String(val || '').trim())
      .map(([key]) => `{{${key}}}`);
    if (missingVars.length > 0) {
      alert(`Faltan valores para las variables: ${missingVars.join(', ')}`);
      return;
    }

    setSendingTemplate(true);
    try {
      // Gate: ¿hay con qué enviar? El backend deriva to/recipient del doc del chat, así que la
      // UI ya no exige teléfono — un chat sólo-BSUID puede recibir plantillas no-auth.
      const recipientId = resolveRecipientId();
      if (!recipientId) {
        alert('No se puede enviar: el chat no tiene teléfono ni BSUID.');
        setSendingTemplate(false);
        return;
      }
      // Pre-check de UX: las plantillas de autenticación NO admiten BSUID. Si el chat es
      // sólo-BSUID (sin teléfono) y la plantilla es AUTHENTICATION, avisar sin ir al backend
      // (el backend igualmente lo rechaza; esto es feedback inmediato).
      const phoneForTemplate = resolvePhoneForTemplate();
      if (!phoneForTemplate && selectedTemplate.category === 'AUTHENTICATION') {
        alert('Las plantillas de autenticación requieren teléfono, y este chat sólo tiene BSUID (cliente con username).');
        setSendingTemplate(false);
        return;
      }
      const orgId = context.mode === 'organization' ? context.orgId : null;

      // Build components with variables (sorted by placeholder index so {{1}} → param 1, etc.)
      const sortedVarKeys = Object.keys(templateVars).sort((a, b) => Number(a) - Number(b));
      const bodyVars = sortedVarKeys.map((k) => String(templateVars[k]).trim());
      const components = bodyVars.length > 0
        ? [{ type: 'body', parameters: bodyVars.map(v => ({ type: 'text', text: v })) }]
        : [];

      await waCloudAPI.sendTemplateMessage({
        phoneNumberId: waPhoneNumberId,
        recipientPhone: phoneForTemplate || undefined,  // hint legacy; el backend deriva del chat
        templateName: selectedTemplate.name,
        languageCode: selectedTemplate.language,
        components,
        organizationId: orgId,
        chatId: chatID,
        templateCategory: selectedTemplate.category || null,
      });

      setShowTemplateModal(false);
      setSelectedTemplate(null);
      setTemplateVars({});
      setConversationExpired(false);
    } catch (err) {
      console.error('Error sending template:', err);
      if (err.message?.includes('expirado') || err.message?.includes('CONVERSATION_EXPIRED')) {
        setConversationExpired(true);
      }
      alert('Error al enviar plantilla: ' + err.message);
    } finally {
      setSendingTemplate(false);
    }
  };

  // Channel detection for visual styles
  const chatCanal = (chatInfo?.canal || 'whatsapp').toLowerCase();
  const channelConfig = CHANNEL_CONFIG[chatCanal] || CHANNEL_CONFIG.whatsapp;
  // El BSUID es un identificador de máquina; nunca debe verse en la cabecera. Bajo el esquema
  // BSUID, nombre/remitente/chatID pueden ser el BSUID crudo o su forma sanitizada. Se
  // enmascaran y se cae a username > teléfono > etiqueta genérica.
  const maskBsuid = (v) => (looksLikeBsuid(v) ? null : v);
  const contactDisplayName =
    maskBsuid(chatInfo?.nombre)
    || chatInfo?.username
    || chatInfo?.telefono
    || maskBsuid(chatInfo?.remitente)
    || (looksLikeBsuid(chatID) ? 'Cliente WhatsApp' : chatID);
  const contactInitials = (contactDisplayName || '?')
    .toString()
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(s => s[0]?.toUpperCase() || '')
    .join('') || '?';

  return (
    <div className={styles.chatContainer}>
      {/* Top breadcrumbs (hidden on mobile) */}
      <div className={styles.topBar}>
        <FaHome style={{ cursor: 'pointer' }} onClick={() => navigate('/')} />
        <span className={styles.topBarSep}>/</span>
        <span className="crumb" onClick={() => navigate('/chats')}>{t('title')}</span>
        <span className={styles.topBarSep}>/</span>
        <span style={{ color: '#1e1b4b', fontWeight: 600 }}>{contactDisplayName}</span>
      </div>

      {/* Sticky chat header (WhatsApp-style) */}
      <header className={styles.chatHeader}>
        <button className={styles.backBtn} onClick={() => navigate('/chats')} aria-label={t('detail.backToChats')}>
          <FaArrowLeft />
        </button>
        <div className={styles.contactAvatar}>{contactInitials}</div>
        <div className={styles.contactInfo}>
          <div className={styles.contactName}>{contactDisplayName}</div>
          <div className={styles.contactStatus}>
            <span className={styles.channelIcon}>{channelConfig.icon}</span>
            {channelConfig.label}
          </div>
        </div>
        <select
          className={styles.statusSelect}
          value={estado}
          onChange={(e) => cambiarEstado(e.target.value)}
        >
          {/* Misma fuente única que el filtro y el selector de la tarjeta:
              `utils/estadosDeChat`. Tres copias a mano ya habían divergido una vez. */}
          {ESTADOS_DE_CHAT.map(({ codigo }) => (
            <option key={codigo} value={codigo}>{t(claveDeEtiqueta(codigo))}</option>
          ))}
        </select>
      </header>

      {/* Action bar */}
      <div className={styles.actionBar}>
        <button
          className={styles.actionBtn}
          onClick={toggleBotPause}
          style={chatInfo?.botPaused
            ? { background: '#d4edda', borderColor: '#28a745', color: '#155724' }
            : { background: '#fff3cd', borderColor: '#ffc107', color: '#856404' }}
          title={chatInfo?.botPaused ? t('detail.bot.resume') : t('detail.bot.pause')}
        >
          {chatInfo?.botPaused ? `▶️ ${t('detail.bot.resume')}` : `⏸️ ${t('detail.bot.pause')}`}
        </button>
        {mostrarInterruptor(chatInfo) && (
          <button
            className={styles.actionBtn}
            onClick={toggleSeguimiento}
            data-testid="interruptor-seguimiento"
            title={seguimientoApagado(chatInfo) ? t('detail.seguimiento.on') : t('detail.seguimiento.off')}
          >
            {seguimientoApagado(chatInfo) ? `🔔 ${t('detail.seguimiento.on')}` : `🔕 ${t('detail.seguimiento.off')}`}
          </button>
        )}
        <button className={styles.actionBtn} onClick={() => exportarMensajes('csv')} disabled={exportando}>
          {exportando ? t('detail.exporting') : t('detail.exportCsv')}
        </button>
        <button className={styles.actionBtn} onClick={() => exportarMensajes('xlsx')} disabled={exportando}>
          {exportando ? t('detail.exporting') : t('detail.exportExcel')}
        </button>
        <button className={styles.actionBtn} onClick={() => setMostrarRespuestas((prev) => !prev)}>
          {mostrarRespuestas ? t('detail.hideQuick') : t('detail.quickReplies')}
        </button>
        <button className={styles.actionBtn} onClick={() => setEdicionRespuestas((prev) => !prev)}>
          {edicionRespuestas ? t('detail.closeEdit') : t('detail.editQuick')}
        </button>
        {errorExportar && (
          <span style={{ color: '#dc2626', fontSize: 12 }}>❌ {errorExportar}</span>
        )}

        {/* BLOQUEAR va al final y visualmente aparte: separador, color propio y sin la
            clase `actionBtn` que comparten los demás. Es una acción con consecuencias y
            no debe quedar a un clic de distancia del botón de pausa, que significa otra
            cosa y es trivialmente reversible.
            Desbloquear vive en el MISMO sitio, no escondido: si cuesta más deshacer que
            hacer, la función se usa con miedo o no se usa. */}
        <span style={{ flex: 1 }} />
        {/* La PALABRA depende de lo que realmente va a pasar. Ver
            `utils/bloqueoDeContacto.js`: donde no hay bloqueo aguas arriba, esto dice
            Silenciar, porque la persona va a poder seguir escribiendo. */}
        <button
          onClick={alternarBloqueo}
          disabled={bloqueando}
          title={chatInfo?.contactoBloqueado
            ? t(`block.unblock${admiteBloqueoReal(chatInfo) ? '' : 'Silenced'}`)
            : t(`block.${admiteBloqueoReal(chatInfo) ? 'block' : 'silence'}`)}
          style={{
            border: '1px solid',
            borderColor: chatInfo?.contactoBloqueado ? '#2f855a' : '#c53030',
            background: chatInfo?.contactoBloqueado ? '#f0fff4' : '#fff5f5',
            color: chatInfo?.contactoBloqueado ? '#2f855a' : '#c53030',
            borderRadius: 6,
            padding: '6px 12px',
            fontSize: 13,
            fontWeight: 600,
            cursor: bloqueando ? 'wait' : 'pointer',
          }}
        >
          {chatInfo?.contactoBloqueado
            ? `↩️ ${t(`block.unblock${admiteBloqueoReal(chatInfo) ? '' : 'Silenced'}`)}`
            : `${admiteBloqueoReal(chatInfo) ? '🚫' : '🔇'} ${t(`block.${admiteBloqueoReal(chatInfo) ? 'block' : 'silence'}`)}`}
        </button>
      </div>

      {/* El aviso de bloqueado. Va ARRIBA del hilo y no se puede perder de vista: un
          operador que escriba en un chat bloqueado y no vea respuesta del bot tiene que
          entender por qué sin preguntarle a nadie. */}
      {chatInfo?.contactoBloqueado === true && (
        <div style={{
          padding: '10px 16px',
          background: admiteBloqueoReal(chatInfo) ? '#fff5f5' : '#fffaf0',
          borderLeft: `4px solid ${admiteBloqueoReal(chatInfo) ? '#c53030' : '#dd6b20'}`,
          color: admiteBloqueoReal(chatInfo) ? '#822727' : '#7b341e',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          fontSize: 14,
        }}>
          {/* El aviso dice lo que ocurre, no una frase genérica: si sólo está
              silenciado, tiene que quedar claro que la persona PUEDE seguir escribiendo,
              porque sus mensajes van a seguir apareciendo aquí abajo. */}
          <span>
            {admiteBloqueoReal(chatInfo) ? '🚫 ' : '🔇 '}
            {t(`block.banner${admiteBloqueoReal(chatInfo) ? '' : 'Silenced'}`)}
          </span>
          <button
            onClick={alternarBloqueo}
            disabled={bloqueando}
            style={{
              background: '#2f855a', color: 'white', border: 'none', borderRadius: 4,
              padding: '6px 14px', cursor: bloqueando ? 'wait' : 'pointer',
              fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap',
            }}
          >
            ↩️ {t(`block.unblock${admiteBloqueoReal(chatInfo) ? '' : 'Silenced'}`)}
          </button>
        </div>
      )}

      {/* Bot paused banner */}
      {chatInfo?.botPaused && (
        <div style={{
          padding: '10px 16px',
          background: '#fff3cd',
          borderLeft: '4px solid #ffc107',
          color: '#856404',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: 14,
        }}>
          {/* Por QUIÉN está pausado (C3g, 27-oct-2026): si lo pausó un echo de la app WhatsApp Business
              (`botPausedBy 'app_business'`, pausaPorEcho.js) nadie del inbox está atendiendo; decir «estás
              atendiendo manualmente» mentía (prueba de humo 2). El resto de pausas, el texto de siempre. */}
          <span>⏸️ {t(claveDelBannerDePausa(chatInfo))}</span>
          <button
            onClick={toggleBotPause}
            style={{
              background: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              padding: '6px 14px',
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            ▶️ {t('detail.bot.resume')}
          </button>
        </div>
      )}

      {/* El bot se rindió acá. Banner + salida manual.
          El botón existe para el caso que la limpieza automática no cubre: el
          agente resolvió por fuera del inbox (llamó por teléfono, lo atendió otro).
          Sin él, esos casos quedan encendidos para siempre y el filtro se llena de
          conversaciones ya resueltas — que es exactamente cómo muere una bandeja
          de "requiere atención". */}
      {/* Banner de VENTA. Usa la MISMA función pura que el badge de la lista y
          que el filtro del inbox — un solo lugar, tres consumidores. */}
      {estadoSeguimientoVenta(chatInfo) !== 'ninguna' && (
        <div style={{
          padding: '10px 16px',
          background: estadoSeguimientoVenta(chatInfo) === 'vencida' ? '#edf2f7' : '#f0fff4',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px'
        }}>
          <div style={{ fontSize: '14px', color: '#22543d' }}>
            {estadoSeguimientoVenta(chatInfo) === 'vencida' ? '💤' : '💰'}{' '}
            <strong>
              {totalDeVentas(chatInfo) > 1
                ? `${totalDeVentas(chatInfo)} compras`
                : 'Compró'}
            </strong>
            {ultimaVenta(chatInfo)?.orderNumber && (
              <> · última {ultimaVenta(chatInfo).orderNumber}</>
            )}
            {ultimaVenta(chatInfo)?.total && (
              <> por {ultimaVenta(chatInfo).total} {ultimaVenta(chatInfo).currency || ''}</>
            )}
          </div>
          <button
            onClick={descartarSeguimientoVenta}
            style={{
              border: 'none', background: 'transparent', cursor: 'pointer',
              color: '#4a5568', fontSize: '13px', textDecoration: 'underline'
            }}
          >
            Quitar marca
          </button>
        </div>
      )}

      {/* Banner de AGENDA. Misma función pura que la píldora de la lista y que el
          filtro `soloConAgenda` — un solo lugar, tres consumidores, igual que la venta.

          NO lleva «Quitar marca» y es deliberado: la de venta lo necesita porque su
          hecho se persiste en el chat y no se deshace solo. Ésta se CALCULA de
          `{tenant}/bookings`, así que cancelar la cita ya la retira. Un botón de
          descarte aquí crearía justo el estado que este diseño evita: un chat con cita
          viva y la marca apagada a mano. */}
      {(() => {
        // UNA sola llamada: qué cita y con qué rótulo lo decide `citaDestacada`, no esta
        // pantalla. Antes aquí y en `Chats.js` se componía `proxima || pasada` a mano —
        // dos copias de la misma regla, que es donde dos consumidores divergen sin que
        // nada se ponga rojo.
        const { estado: estadoAgenda, cita } = citaDestacada(reservasDelChat);
        if (estadoAgenda === 'ninguna') return null;
        const total = totalDeReservas(reservasDelChat);
        const porEstado = {
          proxima:   { icono: '📅', fondo: '#ebf8ff', color: '#1a365d', titulo: 'Cita agendada' },
          pasada:    { icono: '🗓️', fondo: '#edf2f7', color: '#4a5568', titulo: 'Cita pasada' },
          cancelada: { icono: '🚫', fondo: '#fff5f5', color: '#822727', titulo: 'Cita cancelada' },
        }[estadoAgenda];
        return (
          <div style={{
            padding: '10px 16px',
            background: porEstado.fondo,
            borderBottom: '1px solid #e2e8f0',
            display: 'flex', alignItems: 'center', gap: '12px'
          }}>
            <div style={{ fontSize: '14px', color: porEstado.color }}>
              {porEstado.icono} <strong>{porEstado.titulo}</strong>
              {cita?.date && (
                <> · {cita.date}{cita.time ? ` ${cita.time}` : ''}
                  {cita.bookingTimeZone ? ` (${cita.bookingTimeZone})` : ''}</>
              )}
              {cita?.title && cita.title !== 'Reserva' && (<> · {cita.title}</>)}
              {total > 1 && (
                <span style={{ fontWeight: 500, opacity: 0.85 }}> · {total} citas en total</span>
              )}
            </div>
          </div>
        );
      })()}

      {/* Banner de TRASPASO. Cuelga del hecho que escribió `solicitarTraspaso`, no de
          lo que el bot dijera. Sin botón de cierre: lo resuelve responder en el chat,
          que es lo que el job comprueba para apagar la alerta del Dashboard. Un botón
          de «marcar atendido» dejaría cerrar el aviso sin contestarle a nadie. */}
      {estadoSeguimientoTraspaso(chatInfo) !== 'ninguno' && (() => {
        const pendiente = estadoSeguimientoTraspaso(chatInfo) === 'pendiente';
        const plazo = plazoPrometido(chatInfo);
        const motivo = motivoDeTraspaso(chatInfo);
        const demora = demoraDeTraspaso(chatInfo);
        return (
          <div style={{
            padding: '10px 16px',
            background: pendiente ? '#fffaf0' : '#e6fffa',
            borderLeft: `4px solid ${pendiente ? '#dd6b20' : '#319795'}`,
            borderBottom: '1px solid #e2e8f0',
            display: 'flex', alignItems: 'center', gap: '12px'
          }}>
            <div style={{ fontSize: '14px', color: pendiente ? '#7b341e' : '#234e52' }}>
              {pendiente ? '🙋' : '✅'}{' '}
              <strong>{pendiente ? 'Esperando a una persona' : 'Traspaso atendido'}</strong>
              {motivo && (<> · {motivo}</>)}
              {pendiente && plazo && (
                <> · se prometió {plazo.dia}{' '}
                  {plazo.enCurso ? `antes de las ${plazo.fin}` : `entre las ${plazo.inicio} y las ${plazo.fin}`}</>
              )}
              {/* Sin plazo NO se calla: que no se prometiera hora es justo lo que evita
                  que el operador crea que llega tarde. Y señala la configuración que
                  falta, que es lo que lo arregla. */}
              {pendiente && !plazo && (
                <span style={{ fontWeight: 500, opacity: 0.85 }}>
                  {' '}· sin plazo prometido (este bot no tiene horario de atención configurado)
                </span>
              )}
              {!pendiente && Number.isFinite(demora) && (
                <span style={{ fontWeight: 500, opacity: 0.85 }}>
                  {' '}· respondido en {Math.max(1, Math.round(demora / 60000))} min
                </span>
              )}
            </div>
          </div>
        );
      })()}

      {chatInfo?.requiereAtencion === true && (
        <div style={{
          padding: '10px 16px',
          background: '#fed7d7',
          borderLeft: '4px solid #e53e3e',
          color: '#822727',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          fontSize: 14,
        }}>
          <span>
            ⚠️ {t(`attention.motivos.${chatInfo.atencionMotivo}`, {
              defaultValue: t('attention.badge')
            })}
            {chatInfo.atencionDesde?.toDate && (
              <strong> · {formatRelativeTime(chatInfo.atencionDesde.toDate(), t)}</strong>
            )}
            {' — '}{t('attention.bannerHint')}
          </span>
          <button
            onClick={() => marcarAtendido('manual')}
            style={{
              background: '#2f855a',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              padding: '6px 14px',
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 600,
              whiteSpace: 'nowrap',
            }}
          >
            ✓ {t('attention.markHandled')}
          </button>
        </div>
      )}

      {/* Messages area */}
      <div className={styles.messagesArea}>
        {error && (
          <div className={styles.errorState}>
            <div style={{ fontSize: 24, marginBottom: 8 }}>⚠️</div>
            <p style={{ margin: 0, fontWeight: 500 }}>{error}</p>
            <button
              onClick={() => window.location.reload()}
              style={{
                marginTop: 8,
                padding: '6px 12px',
                background: '#c33',
                color: 'white',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
              }}
            >
              {t('detail.reloadPage')}
            </button>
          </div>
        )}

        {loadingMensajes ? (
          <div className={styles.loadingState}>
            <div className={styles.loadingIcon}>🔄</div>
            <p style={{ marginTop: 12 }}>{t('detail.loadingMessages')}</p>
          </div>
        ) : mensajes.length === 0 && !error ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyStateIcon}>💬</div>
            <p style={{ margin: 0, fontSize: 16 }}>{t('detail.noMessages')}</p>
            <p style={{ margin: '8px 0 0 0', fontSize: 13, color: '#94a3b8' }}>
              {t('detail.sendFirstMessage')}
            </p>
          </div>
        ) : null}

        {!loadingMensajes && !error && mensajesParaRender.map((msg) => {
          const direction = msg.direction || msg.direccion;
          const esUsuario = direction === 'in' || direction === 'entrante';
          const esAsistente = direction === 'out' || direction === 'saliente';
          // Outbound origin: 'agent' set by sendMessage.js for human-typed messages;
          // bot path writes remitente: 'bot' + esRespuestaBot: true. Legacy messages
          // without either field fall back to the bot label (the dominant historical case).
          const isAgentMsg = esAsistente && msg.remitente === 'agent';
          const isBotMsg = esAsistente && !isAgentMsg;
          const rowClass = esAsistente ? styles.out : styles.in;
          const bubbleSideClass = esAsistente ? styles.out : styles.in;
          const channelClass = esAsistente ? (styles[chatCanal] || styles.whatsapp) : '';
          const isTemp = typeof msg.id === 'string' && msg.id.startsWith('temp_');
          const isPending = isTemp && msg.pending;
          const isFailed = isTemp && msg.failed;
          const bubbleStyle = isPending ? { opacity: 0.6 } : (isFailed ? { opacity: 0.85, borderLeft: '3px solid #dc2626' } : null);

          return (
            <div key={msg.id} className={`${styles.bubbleRow} ${rowClass}`}>
              <div className={`${styles.bubble} ${bubbleSideClass} ${channelClass}`} style={bubbleStyle}>
                {esUsuario && (
                  <div className={styles.bubbleSender}>
                    {msg.pushName || msg.from || 'Cliente'}
                  </div>
                )}
                {esAsistente && !isTemp && (
                  <div className={styles.bubbleSender} style={{ opacity: 0.75, fontSize: '0.75rem' }}>
                    {isBotMsg ? `🤖 ${t('detail.senderBot')}` : `👤 ${t('detail.senderAgent')}`}
                  </div>
                )}

                {editandoMensajeID === msg.id ? (
                  <>
                    <input
                      className={styles.editInput}
                      value={mensajeEditado}
                      onChange={(e) => setMensajeEditado(e.target.value)}
                    />
                    <div style={{ textAlign: 'right' }}>
                      <FaCheck onClick={() => guardarEdicion(msg.id)} style={{ color: 'green', cursor: 'pointer', marginRight: 8 }} />
                      <FaTimes onClick={() => setEditandoMensajeID(null)} style={{ color: 'red', cursor: 'pointer' }} />
                    </div>
                  </>
                ) : (
                  <>
                    <CuerpoMensaje msg={msg} styles={styles} t={t} media={media} />
                    <BotonesDeMensaje msg={msg} t={t} />

                    <div className={styles.bubbleMeta}>
                      <span>{formatearFecha(msg.timestamp)}</span>
                      {/* El acuse sale del ESTADO REAL, no de que el mensaje exista. Hasta el
                          24-ago-2026 esto era `esAsistente && !isTemp && ✓✓`: un doble check
                          constante, disfrazado de acuse. Como el envío de WhatsApp es asíncrono
                          —Meta devuelve 200 con `wamid`, que significa aceptado y NO entregado—,
                          un mensaje rechazado un segundo después seguía luciendo entregado.
                          Medido: 3 de 16 salientes (19%) estaban así.
                      
                          `fallido` usa el MISMO ⚠️ que los temporales de abajo, y no un check
                          distinto: un check gris sigue pareciendo un acuse, y lo que se corrige
                          es justo que el cliente crea algo que no es.
                      
                          Sin `estado` —mensajes anteriores a este cambio, y los canales que no
                          mandan acuses— NO se pinta nada: no saber no es haber entregado. */}
                      {esAsistente && !isTemp && msg.estado === 'entregado' && (
                        <span style={{ color: '#4f46e5' }} title={t('detail.delivered')}>✓✓</span>
                      )}
                      {esAsistente && !isTemp && msg.estado === 'enviado' && (
                        <span style={{ color: '#94a3b8' }} title={t('detail.sentOnly')}>✓</span>
                      )}
                      {esAsistente && !isTemp && msg.estado === 'fallido' && (
                        <span style={{ color: '#dc2626' }} title={msg.errorEntrega?.details || t('detail.deliveryFailed')}>
                          ⚠️ {t('detail.deliveryFailed')}
                        </span>
                      )}
                      {isPending && <span style={{ color: '#94a3b8' }}>⏱</span>}
                      {isFailed && <span style={{ color: '#dc2626' }}>⚠️</span>}
                    </div>

                    {isFailed && (
                      <div style={{ marginTop: 6, fontSize: 12, color: '#dc2626' }}>
                        {msg.errorMsg || 'Error al enviar'}
                        {msg.retryable && (
                          <button
                            onClick={() => reintentarMensaje(msg.id)}
                            style={{ marginLeft: 8, padding: '2px 8px', fontSize: 11, background: '#dc2626', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                          >
                            Reintentar
                          </button>
                        )}
                      </div>
                    )}

                    {esAsistente && !isTemp && (
                      <div className={styles.bubbleActions}>
                        {/* Sin lápiz sobre un echo SIN TEXTO de la app Business (C3d): el input se llenaría
                            con el marcador `[audio]` y la burbuja nueva no pinta `text`, así que la
                            edición no cambiaría nada visible. El resto, igual que siempre. */}
                        {!esEchoSinTexto(msg) && <FaEdit className={styles.bubbleActionIcon} onClick={() => iniciarEdicion(msg)} />}
                        <FaTrash className={`${styles.bubbleActionIcon} ${styles.danger}`} onClick={() => eliminarMensaje(msg.id)} />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
        <div ref={mensajesRef} />
      </div>

      {/* Quick replies panel */}
      {mostrarRespuestas && (
        <div className={styles.quickRepliesPanel}>
          {respuestasRapidas.length === 0 ? (
            <p style={{ margin: 0, fontSize: 13, color: '#64748b' }}>{t('detail.noQuickReplies')}</p>
          ) : (
            <ul className={styles.quickRepliesList}>
              {respuestasRapidas.map((res) => (
                <li key={res.id} className={styles.quickReplyItem} onClick={() => agregarRespuestaRapida(res.texto)}>
                  <FaReply style={{ color: '#4f46e5' }} /> {res.texto}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Manage quick replies panel */}
      {edicionRespuestas && (
        <div className={styles.editPanel}>
          <h4 style={{ margin: '0 0 10px 0', fontSize: 14 }}>{t('detail.manageQuickReplies')}</h4>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <input
              type="text"
              placeholder={t('detail.newReplyPlaceholder')}
              value={nuevaRespuesta}
              onChange={(e) => setNuevaRespuesta(e.target.value)}
              style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: '1px solid #cbd5e0', fontSize: 13 }}
            />
            <button className={`${styles.actionBtn} ${styles.primary}`} onClick={agregarRespuesta}>
              <FaPlus /> {t('detail.add')}
            </button>
          </div>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {respuestasRapidas.map((res) => (
              <li key={res.id} style={{ marginBottom: 6, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                {editandoRespuestaID === res.id ? (
                  <>
                    <input
                      type="text"
                      value={textoEditado}
                      onChange={(e) => setTextoEditado(e.target.value)}
                      style={{ flex: 1, padding: '4px 8px', borderRadius: 4, border: '1px solid #cbd5e0' }}
                    />
                    <FaCheck onClick={() => guardarRespuesta(res.id)} style={{ color: 'green', cursor: 'pointer' }} />
                    <FaTimes onClick={() => setEditandoRespuestaID(null)} style={{ color: 'red', cursor: 'pointer' }} />
                  </>
                ) : (
                  <>
                    <span style={{ flex: 1 }}>{res.texto}</span>
                    <FaEdit onClick={() => { setEditandoRespuestaID(res.id); setTextoEditado(res.texto); }} style={{ cursor: 'pointer', color: '#4f46e5' }} />
                    <FaTrash onClick={() => eliminarRespuesta(res.id)} style={{ cursor: 'pointer', color: '#dc2626' }} />
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Expired conversation banner */}
      {conversationExpired && isWaCloudChat && (
        <div className={styles.expiredBanner}>
          <span>{t('detail.expiredConversation')}</span>
          <button className={styles.expiredBtn} onClick={openTemplateModal}>
            {t('detail.sendTemplate')}
          </button>
        </div>
      )}

      {/* Instagram y Messenger: la MISMA ventana de 24 h, pero SIN plantillas con las
          que reabrirla. Por eso este banner no lleva botón: ofrecer una acción que no
          existe es peor que no ofrecer ninguna.

          Va aparte y no dentro del de arriba porque el de arriba está gateado por
          `isWaCloudChat`: sin esta rama, marcar la conversación como expirada en estos
          dos canales haría DESAPARECER el mensaje sin decir nada — peor que el volcado
          de JSON que este trabajo corrige. */}
      {conversationExpired && !isWaCloudChat && (
        <div className={styles.expiredBanner} role="status">
          <span>{t('detail.expiredNoTemplate')}</span>
        </div>
      )}

      {/* Aviso de cuenta desconectada.
          Va ANTES del input y lo inhabilita, no después del envío: un chat cuya cuenta se
          desconectó NO se arregla reintentando, así que dejar escribir y fallar luego sería
          el mismo defecto que este trabajo corrige, un paso más tarde. Y por eso el texto
          dice qué hacer —reconectar— en vez de «error»: un error invita a reintentar. */}
      {cuentaHuerfana === true && (
        <div className={styles.cuentaDesconectada} role="status">
          Esta cuenta ya no está conectada, así que no se puede responder por aquí.
          Reconéctala en Canales para retomar la conversación.
        </div>
      )}

      {/* Sticky input bar */}
      <div className={styles.inputBar}>
        {isWaCloudChat && (
          <button className={styles.templateBtn} onClick={openTemplateModal} title="Enviar plantilla">
            <FaClipboardList />
          </button>
        )}
        <input
          type="text"
          className={styles.messageInput}
          value={nuevoMensaje}
          onChange={(e) => setNuevoMensaje(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && enviarMensaje()}
          placeholder={cuentaHuerfana === true ? 'Cuenta desconectada' : t('detail.messagePlaceholder')}
          maxLength={1000}
          disabled={cuentaHuerfana === true}
        />
        <button
          className={styles.sendBtn}
          onClick={enviarMensaje}
          disabled={enviando || cuentaHuerfana === true}
          aria-label={t('detail.send')}
        >
          <FaPaperPlane />
        </button>
      </div>

      {/* Template Modal */}
      {showTemplateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '12px',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '80vh',
            overflow: 'auto',
            padding: '1.5rem'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0 }}>
                {selectedTemplate ? t('detail.configureTemplate') : t('detail.selectTemplate')}
              </h3>
              <button
                onClick={() => { setShowTemplateModal(false); setSelectedTemplate(null); }}
                style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#666' }}
              >
                &times;
              </button>
            </div>

            {loadingTemplates && (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>
                {t('detail.loadingTemplates')}
              </div>
            )}

            {!loadingTemplates && !selectedTemplate && templates.length === 0 && (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>
                {t('detail.noTemplatesAvailable')}
              </div>
            )}

            {/* Template list */}
            {!selectedTemplate && templates.length > 0 && (
              <div style={{ display: 'grid', gap: '8px' }}>
                {templates.map((tpl, idx) => {
                  const bodyComp = tpl.components?.find(c => c.type === 'BODY');
                  const bodyText = bodyComp?.text || '';
                  // DESHABILITADA CON SU MOTIVO, no escondida: el operador tiene que
                  // entender por qué no puede enviarla. El backend vuelve a decidir.
                  const veredicto = veredictoDePlantilla(tpl, chatInfo);
                  const bloqueada = !veredicto.permitido;
                  return (
                    <div
                      key={`${tpl.name}-${tpl.language}-${idx}`}
                      onClick={() => { if (!bloqueada) selectTemplate(tpl); }}
                      aria-disabled={bloqueada}
                      title={bloqueada ? veredicto.mensaje : undefined}
                      style={{
                        padding: '0.75rem',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        cursor: bloqueada ? 'not-allowed' : 'pointer',
                        opacity: bloqueada ? 0.55 : 1,
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={e => { if (!bloqueada) e.currentTarget.style.backgroundColor = '#f8f9fa'; }}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = '#fff'}
                    >
                      {bloqueada && (
                        <div style={{ fontSize: 12, color: '#c92a2a', marginBottom: 4, fontWeight: 600 }}>
                          🚫 {veredicto.mensaje}
                        </div>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                        <strong>{tpl.name}</strong>
                        <span style={{
                          padding: '1px 6px',
                          backgroundColor: '#d4edda',
                          color: '#155724',
                          borderRadius: '10px',
                          fontSize: '0.7rem'
                        }}>
                          {tpl.language}
                        </span>
                      </div>
                      {bodyText && (
                        <div style={{ fontSize: '0.85rem', color: '#666', overflow: 'hidden', maxHeight: '40px' }}>
                          {bodyText}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Selected template: variable inputs + preview */}
            {selectedTemplate && (
              <div>
                <button
                  onClick={() => { setSelectedTemplate(null); setTemplateVars({}); }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#1a73e8',
                    cursor: 'pointer',
                    padding: 0,
                    marginBottom: '1rem',
                    fontSize: '0.9rem'
                  }}
                >
                  {t('detail.backToList')}
                </button>

                <div style={{ marginBottom: '1rem' }}>
                  <strong>{selectedTemplate.name}</strong>
                  <span style={{ marginLeft: '0.5rem', fontSize: '0.85rem', color: '#666' }}>
                    ({selectedTemplate.language})
                  </span>
                </div>

                {/* Variable inputs */}
                {Object.keys(templateVars).length > 0 && (
                  <div style={{ marginBottom: '1rem' }}>
                    <p style={{ fontSize: '0.9rem', color: '#555', marginBottom: '0.5rem' }}>
                      {t('detail.completeVariables')}
                    </p>
                    {/* ORDEN NUMÉRICO, no lexicográfico. Con `.sort()` a secas, una
                        plantilla de 10+ variables pondría {{10}} antes que {{2}} — y el
                        ENVÍO ya ordena por número, así que el operador vería los campos
                        en un orden y sus valores irían en otro. Hoy el máximo son 4 y no
                        se nota; es justo la clase de defecto que aparece al crecer. */}
                    {Object.keys(templateVars).sort((a, b) => Number(a) - Number(b)).map(key => {
                      // La etiqueta sale de la plantilla, no de la posición. Si PUX no la
                      // declara —plantillas del propio cliente— se cae a «Variable {{N}}»,
                      // que es el comportamiento de siempre.
                      const clave = claveDeEtiquetaDeVariable(selectedTemplate?.name, key);
                      const etiqueta = clave ? t(`detail.templateVars.${clave}`) : `Variable {{${key}}}`;
                      return (
                      <div key={key} style={{ marginBottom: '0.5rem' }}>
                        <label style={{ fontSize: '0.85rem', color: '#333', display: 'block', marginBottom: '0.25rem' }}>
                          {etiqueta}
                        </label>
                        <input
                          type="text"
                          value={templateVars[key]}
                          onChange={(e) => setTemplateVars(prev => ({ ...prev, [key]: e.target.value }))}
                          placeholder={etiqueta}
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            borderRadius: '6px',
                            border: '1px solid #ccc',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>
                      );
                    })}
                  </div>
                )}

                {/* Preview */}
                <div style={{
                  padding: '1rem',
                  backgroundColor: '#dcf8c6',
                  borderRadius: '8px',
                  borderLeft: '4px solid #25D366',
                  marginBottom: '1rem',
                  fontSize: '0.9rem',
                  whiteSpace: 'pre-wrap'
                }}>
                  <div style={{ fontSize: '0.75rem', color: '#555', marginBottom: '0.5rem', fontWeight: '600' }}>
                    {t('detail.preview')}
                  </div>
                  {getTemplatePreview()}
                </div>

                <button
                  onClick={handleSendTemplate}
                  disabled={sendingTemplate}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    backgroundColor: sendingTemplate ? '#6c757d' : '#25D366',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: sendingTemplate ? 'not-allowed' : 'pointer',
                    fontSize: '1rem',
                    fontWeight: '600'
                  }}
                >
                  {sendingTemplate ? tc('sending') : t('detail.sendTemplate')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatDetalle;